import { rupiah, monthLabel, dateLabel, MONTHS_ID, ageLabel, UNDERSTANDING } from "@/lib/format";
import { Sparkles, Award, Star } from "lucide-react";

const A4P = { w: 210, h: 297 };
export const PAGE_SIZES = {
  formulir: A4P, profil: A4P, kwitansi: A4P, prestasi: A4P, iuran: A4P, "laporan-jurnal": A4P, jadwal: A4P,
  "rekap-kehadiran": A4P, "rekap-pembayaran": A4P,
  sertifikat: { w: 297, h: 210, fixed: true },
};

const FONT = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const photoSrc = (p) => (p ? `${process.env.REACT_APP_BACKEND_URL}${p}` : "");
export const signer = (settings) => settings?.admin_name || "Krida Salsabila";
const brand = (settings) => (settings?.name || "Bimbel Gemma").replace(/^bimbel\s*/i, "");
const today = () => dateLabel(new Date().toISOString());

export function Sheet({ size, pad = 15, children, testId, className = "" }) {
  const s = PAGE_SIZES[size];
  return (
    <div className={`print-sheet bg-white text-slate-800 mx-auto shadow-sm flex flex-col ${className}`} data-testid={testId}
      style={{ ...FONT, "--sheet-w": `${s.w}mm`, "--sheet-h": `${s.h}mm`, width: `${s.w}mm`, height: `${s.h}mm`, padding: `${pad}mm`, overflow: "hidden", boxSizing: "border-box" }}>
      {children}
    </div>
  );
}

/* ---------- STANDAR HEADER & FOOTER ---------- */
export const Header = ({ settings }) => (
  <div className="flex items-center gap-4 border-b-[3px] border-orange-400 pb-3 mb-5 shrink-0">
    <img src="/mascot.png" alt="logo" className="h-16 w-16 object-contain" />
    <div className="leading-tight flex-1">
      <div className="font-display text-2xl font-bold text-[#1e2a78]"><span className="text-orange-500">Bimbel</span> {brand(settings)}</div>
      <div className="text-xs text-slate-500">{settings?.tagline}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{settings?.address}</div>
    </div>
    <div className="text-right text-[10px] text-slate-500 leading-tight">
      <div>WA/Telp: {settings?.phone}</div>
      {settings?.email && <div>{settings.email}</div>}
    </div>
  </div>
);

export const Footer = ({ settings }) => (
  <div className="mt-auto pt-3 border-t border-amber-200 flex items-center justify-between text-[9px] text-slate-400 shrink-0">
    <div>{settings?.name || "Bimbel Gemma"} · {settings?.address}</div>
    <div>Dicetak {today()}</div>
  </div>
);

export const Title = ({ children, color = "bg-orange-100 text-orange-600", sub }) => (
  <div className="text-center mb-5 shrink-0">
    <div className={`inline-block px-5 py-1 rounded-full font-display font-bold text-base ${color}`}>{children}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const SignCol = ({ role, name, testId }) => (
  <div className="text-center min-w-[150px]" data-testid={testId}>
    <div className="text-xs text-slate-500 mb-14">{role}</div>
    <div className="font-display font-bold text-xs border-t-2 border-slate-300 pt-1 px-4 min-h-[20px]">{name}</div>
  </div>
);

export const Signatures = ({ left, right }) => (
  <div className={`flex ${left && right ? "justify-between" : "justify-end"} items-end mt-8 mb-4 px-6 shrink-0`} data-testid="doc-signatures">
    {left && <SignCol role={left.role} name={left.name} testId="sign-left" />}
    {right && <SignCol role={right.role} name={right.name} testId="sign-right" />}
  </div>
);

const StudentBox = ({ student, extra }) => (
  <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-amber-50 shrink-0">
    <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-display text-xl font-bold shrink-0">
      {student?.photo ? <img src={photoSrc(student.photo)} alt="" className="w-full h-full object-cover" /> : (student?.name || "?").charAt(0)}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-display text-lg font-bold leading-tight">{student?.name}</div>
      <div className="text-xs text-slate-500">{student?.class_label || student?.level}{student?.birth_date ? ` · ${ageLabel(student.birth_date)}` : ""}</div>
    </div>
    {extra}
  </div>
);

const th = "py-2 px-2 text-left font-semibold text-[11px]";
const td = "py-1.5 px-2 border-b border-slate-100 text-[11px] align-top";
const parentName = (s) => s?.mother_name || s?.father_name || "";

/* ---------- FORMULIR PENDAFTARAN (blanko kosong) ---------- */
const DEFAULT_TERMS = [
  "Iuran bulanan Rp 150.000 dibayarkan paling lambat tanggal 15 setiap bulan.",
  "Siswa mengikuti 3 sesi belajar per minggu, 1 jam per sesi, sesuai jadwal yang disepakati.",
  "Ketidakhadiran wajib diinformasikan kepada pengajar; setiap sesi yang tidak dihadiri hanya mendapat 1 (satu) kali jadwal pengganti.",
  "Jadwal pengganti yang tidak dihadiri dianggap sesi telah diikuti.",
  "Orang tua/wali bersedia menerima informasi jadwal, tagihan, dan laporan belajar melalui WhatsApp.",
  "Siswa yang berhenti sementara dapat mengaktifkan kembali pendaftaran tanpa biaya tambahan.",
];
const Blank = ({ label, w = "" }) => (
  <div className={`flex items-end gap-2 ${w}`}><span className="text-[11px] text-slate-600 w-36 shrink-0">{label}</span><span className="flex-1 border-b border-dotted border-slate-500 min-h-[22px]" /></div>
);

export function FormulirPendaftaran({ settings }) {
  const terms = settings?.terms?.length ? settings.terms : DEFAULT_TERMS;
  return (
    <Sheet size="formulir" testId="doc-formulir">
      <Header settings={settings} />
      <Title color="bg-sky-100 text-sky-700" sub="Isi dengan huruf cetak · lampirkan fotokopi akta kelahiran / KK bila ada">FORMULIR PENDAFTARAN SISWA BARU</Title>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-5">
        <Blank label="Nama Lengkap" w="col-span-2" />
        <Blank label="Jenjang (TPA/TK/SD)" /><Blank label="Kelas" />
        <Blank label="Jenis Kelamin" /><Blank label="Tempat Lahir" />
        <Blank label="Tanggal Lahir" /><Blank label="Tanggal Daftar" />
        <Blank label="Alamat" w="col-span-2" />
        <Blank label="Nama Ayah" /><Blank label="Nama Ibu" />
        <Blank label="No. WhatsApp Wali" /><Blank label="Sekolah Asal" />
        <Blank label="Pilihan Hari (3 hari)" w="col-span-2" /><Blank label="Pilihan Jam" w="col-span-2" />
      </div>
      <div className="rounded-xl border border-amber-200 p-4">
        <div className="font-display font-bold text-sm text-[#1e2a78] mb-2">Syarat & Ketentuan</div>
        <ol className="list-decimal pl-4 space-y-1 text-[11px] text-slate-700">{terms.map((t, i) => <li key={i}>{t}</li>)}</ol>
      </div>
      <p className="text-[11px] text-slate-600 mt-3">Dengan menandatangani formulir ini, orang tua/wali menyatakan telah membaca dan menyetujui syarat & ketentuan di atas.</p>
      <Signatures left={{ role: "Admin", name: signer(settings) }} right={{ role: "Orang Tua / Wali", name: "" }} />
      <Footer settings={settings} />
    </Sheet>
  );
}

/* ---------- PROFIL SISWA ---------- */
export function ProfilSiswa({ settings, student, journals = [], payments = [] }) {
  const rows = [
    ["Nama Lengkap", student?.name], ["Jenjang / Kelas", student?.class_label || student?.level || ""],
    ["Jenis Kelamin", student?.gender === "L" ? "Laki-laki" : "Perempuan"], ["Tempat, Tanggal Lahir", `${student?.birth_place || "-"}, ${student?.birth_date ? dateLabel(student.birth_date) : "-"}`],
    ["Umur", ageLabel(student?.birth_date) || "-"], ["Alamat", student?.address || "-"],
    ["Nama Ayah", student?.father_name || "-"], ["Nama Ibu", student?.mother_name || "-"],
    ["No. WhatsApp Wali", student?.wa || "-"], ["Status", student?.status === "aktif" ? "Aktif" : "Nonaktif"],
    ["Terdaftar", student?.registered_at ? dateLabel(student.registered_at) : "-"], ["Iuran Bulanan", rupiah(student?.monthly_fee || 150000)],
  ];
  const avg = journals.length ? (journals.reduce((a, j) => a + (j.rating || 0), 0) / journals.length).toFixed(1) : "-";
  const lunas = payments.filter((p) => p.status === "lunas").length;
  return (
    <Sheet size="profil" testId="doc-profil">
      <Header settings={settings} />
      <Title color="bg-violet-100 text-violet-700">PROFIL SISWA</Title>
      <div className="flex gap-5 mb-5">
        <div className="w-28 h-36 rounded-xl overflow-hidden bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-display text-4xl font-bold shrink-0 border-4 border-white shadow">
          {student?.photo ? <img src={photoSrc(student.photo)} alt="" className="w-full h-full object-cover" /> : (student?.name || "?").charAt(0)}
        </div>
        <table className="flex-1 text-[12px] self-start"><tbody>
          {rows.map(([k, v]) => <tr key={k} className="border-b border-slate-100"><td className="py-1.5 text-slate-500 w-40">{k}</td><td className="py-1.5 font-medium">: {v}</td></tr>)}
        </tbody></table>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[["Jadwal Belajar", (student?.schedule || []).map((s) => `${s.day} ${s.time}`).join(" · ") || "-"], ["Rata-rata Bintang", `⭐ ${avg}`], ["Bulan Lunas", `${lunas} bulan`]].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center"><div className="text-[10px] text-slate-500">{k}</div><div className="font-display font-bold text-sm mt-0.5">{v}</div></div>
        ))}
      </div>
      {student?.notes && <div className="rounded-xl border border-slate-200 p-3 text-[11px] text-slate-600"><span className="font-semibold">Catatan:</span> {student.notes}</div>}
      <Signatures left={{ role: "Admin Bimbel", name: signer(settings) }} right={{ role: "Orang Tua / Wali", name: parentName(student) }} />
      <Footer settings={settings} />
    </Sheet>
  );
}

/* ---------- KWITANSI ---------- */
export function Kwitansi({ settings, student, payment }) {
  const sisa = Math.max((payment?.amount_due || 0) - (payment?.amount_paid || 0), 0);
  return (
    <Sheet size="kwitansi" testId="doc-kwitansi">
      <Header settings={settings} />
      <Title color="bg-orange-100 text-orange-600" sub={`No: KW/${payment?.month?.replace("-", "")}/${(student?.name || "").slice(0, 3).toUpperCase()} · Tanggal: ${payment?.paid_date ? dateLabel(payment.paid_date) : "-"}`}>KWITANSI PEMBAYARAN</Title>
      <table className="w-full text-[12px] mb-5"><tbody>
        {[["Telah diterima dari", `Wali dari ${student?.name}`], ["Jenjang", student?.class_label || student?.level || ""], ["Untuk pembayaran", `Iuran les bulan ${monthLabel(payment?.month)}`], ["Metode", payment?.method || "-"], ["Status", payment?.status === "lunas" ? "LUNAS" : `Sebagian · sisa ${rupiah(sisa)}`]].map(([k, v], i) => (
          <tr key={i} className="border-b border-slate-100"><td className="py-2 text-slate-500 w-44">{k}</td><td className="py-2 font-medium">: {v}</td></tr>
        ))}
      </tbody></table>
      <div className="px-6 py-4 rounded-2xl bg-emerald-50 border-2 border-emerald-300 w-fit">
        <div className="text-xs text-emerald-600">Jumlah Dibayar</div>
        <div className="font-display text-3xl font-bold text-emerald-700">{rupiah(payment?.amount_paid)}</div>
      </div>
      {payment?.history?.length > 1 && (
        <div className="mt-5">
          <div className="text-xs font-semibold text-slate-600 mb-1">Riwayat pembayaran</div>
          <table className="w-full"><thead><tr className="bg-amber-50 text-slate-600"><th className={th}>Tanggal</th><th className={th}>Nominal</th><th className={th}>Metode</th><th className={th}>Catatan</th></tr></thead>
            <tbody>{payment.history.map((h) => <tr key={h.id}><td className={td}>{dateLabel(h.paid_date)}</td><td className={td}>{rupiah(h.amount)}</td><td className={td}>{h.method || "-"}</td><td className={td}>{h.note || "-"}</td></tr>)}</tbody></table>
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-5">Kwitansi ini dicetak otomatis oleh sistem dan sah tanpa tanda tangan.</p>
      <Footer settings={settings} />
    </Sheet>
  );
}

/* ---------- LEMBAR PRESTASI ---------- */
export function LembarPrestasi({ settings, student, journals = [] }) {
  const avg = journals.length ? (journals.reduce((a, j) => a + (j.rating || 0), 0) / journals.length) : 0;
  return (
    <Sheet size="prestasi" testId="doc-prestasi">
      <Header settings={settings} />
      <Title color="bg-violet-100 text-violet-600">LEMBAR PRESTASI BELAJAR</Title>
      <StudentBox student={student} extra={
        <div className="text-center px-3">
          <div className="flex gap-0.5">{[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div>
          <div className="text-[10px] text-slate-500">Rata-rata {avg.toFixed(1)} · {journals.length} pertemuan</div>
        </div>} />
      <table className="w-full">
        <thead><tr className="bg-violet-50 text-violet-700"><th className={th}>Tanggal</th><th className={th}>Materi</th><th className={`${th} text-center`}>Pemahaman</th><th className={`${th} text-center`}>Bintang</th></tr></thead>
        <tbody>
          {journals.slice(0, 20).map((j) => (
            <tr key={j.id}><td className={`${td} whitespace-nowrap`}>{dateLabel(j.date)}</td><td className={`${td} font-medium`}>{j.material}</td><td className={`${td} text-center`}>{UNDERSTANDING[j.understanding || "paham"].label}</td><td className={`${td} text-center whitespace-nowrap text-amber-500`}>{"★".repeat(j.rating)}<span className="text-slate-200">{"★".repeat(5 - j.rating)}</span></td></tr>
          ))}
          {journals.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400 text-xs">Belum ada catatan prestasi.</td></tr>}
        </tbody>
      </table>
      <Footer settings={settings} />
    </Sheet>
  );
}

/* ---------- SERTIFIKAT (A4 landscape, tanpa header/footer standar) ---------- */
const Seal = () => (
  <div className="w-24 h-24 rounded-full border-4 border-orange-400 flex flex-col items-center justify-center text-orange-500 rotate-[-8deg] opacity-90 bg-white">
    <Sparkles className="w-6 h-6" /><span className="font-display font-bold text-sm mt-1">GEMMA</span><span className="text-[8px] tracking-widest">BIMBEL</span>
  </div>
);
export function Sertifikat({ settings, student }) {
  return (
    <Sheet size="sertifikat" pad={8} testId="doc-sertifikat">
      <div className="h-full border-[6px] border-double border-orange-400 rounded-lg px-10 py-6 text-center relative flex flex-col justify-between">
        <div className="absolute top-5 right-5"><Seal /></div>
        <img src="/mascot.png" alt="logo" className="absolute top-5 left-5 h-20 w-20 object-contain" />
        <div>
          <Award className="w-12 h-12 text-amber-500 mx-auto" />
          <div className="font-display text-3xl font-bold text-slate-900 mt-1 tracking-wide">SERTIFIKAT PENGHARGAAN</div>
          <div className="text-xs text-slate-500 mt-1">{settings?.name || "Bimbel Gemma"} · {settings?.address}</div>
        </div>
        <div>
          <div className="text-slate-500 text-sm">Diberikan kepada:</div>
          <div className="font-display text-5xl font-bold text-orange-600 my-3">{student?.name}</div>
          <div className="text-slate-600 text-sm max-w-xl mx-auto">Atas dedikasi, semangat, dan pencapaian yang membanggakan selama mengikuti kegiatan belajar di <span className="font-bold">{settings?.name || "Bimbel Gemma"}</span> jenjang {student?.class_label || student?.level}.</div>
        </div>
        <div className="flex justify-end px-6">
          <div className="text-center min-w-[180px]" data-testid="sertifikat-sign">
            <div className="text-xs text-slate-600">Kawali, {today()}</div>
            <div className="text-xs text-slate-500 mb-14">Pengajar</div>
            <div className="font-display font-bold text-sm border-t-2 border-slate-300 pt-1 px-4">{signer(settings)}</div>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/* ---------- KARTU IURAN ---------- */
export function KartuIuran({ settings, student, payments = [], year }) {
  const byMonth = {};
  payments.forEach((p) => { byMonth[Number((p.month || "").split("-")[1])] = p; });
  return (
    <Sheet size="iuran" testId="doc-iuran">
      <Header settings={settings} />
      <Title color="bg-sky-100 text-sky-600">KARTU IURAN BULANAN {year}</Title>
      <StudentBox student={student} extra={<div className="text-right text-xs"><div className="text-slate-500">Iuran</div><div className="font-display font-bold">{rupiah(student?.monthly_fee || 150000)}/bln</div></div>} />
      <div className="grid grid-cols-3 gap-3">
        {MONTHS_ID.map((mn, i) => {
          const p = byMonth[i + 1]; const paid = p?.status === "lunas";
          return (
            <div key={i} className={`rounded-xl border-2 p-3 text-center ${paid ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-200"}`}>
              <div className="text-xs font-semibold text-slate-700">{mn}</div>
              {paid ? <div className="mt-1"><div className="w-11 h-11 rounded-full border-2 border-emerald-500 text-emerald-600 flex items-center justify-center font-display font-bold text-[10px] mx-auto rotate-[-12deg]">LUNAS</div><div className="text-[9px] text-slate-400 mt-1">{p.paid_date ? dateLabel(p.paid_date) : ""}</div></div>
                : <div className="mt-1 text-[10px] text-slate-300 h-[58px] flex items-center justify-center">{p ? `${rupiah(p.amount_paid)} dibayar` : "belum"}</div>}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-500 mt-4 text-center">Jatuh tempo tanggal {settings?.due_day || 15} setiap bulan · Transfer: {settings?.bank_name} {settings?.bank_account} a.n. {settings?.bank_holder}</div>
      <Footer settings={settings} />
    </Sheet>
  );
}

/* ---------- LAPORAN JURNAL BULANAN ---------- */
export function LaporanJurnal({ settings, student, journals = [], month }) {
  const avg = journals.length ? (journals.reduce((a, j) => a + (j.rating || 0), 0) / journals.length) : 0;
  return (
    <Sheet size="laporan-jurnal" testId="doc-laporan-jurnal">
      <Header settings={settings} />
      <Title color="bg-violet-100 text-violet-700" sub={`Periode: ${monthLabel(month)}`}>LAPORAN JURNAL BELAJAR BULANAN</Title>
      <StudentBox student={student} extra={<div className="text-center px-3"><div className="font-display text-xl font-bold">{journals.length}</div><div className="text-[10px] text-slate-500">pertemuan</div></div>} />
      <table className="w-full">
        <thead><tr className="bg-violet-50 text-violet-700">
          <th className={th}>Tanggal</th><th className={th}>Materi</th><th className={th}>PR / Tugas</th><th className={th}>Catatan Guru</th><th className={`${th} text-center`}>Pemahaman</th><th className={`${th} text-center`}>Bintang</th>
        </tr></thead>
        <tbody>
          {journals.map((j) => (
            <tr key={j.id}>
              <td className={`${td} whitespace-nowrap`}>{dateLabel(j.date)}</td><td className={`${td} font-medium`}>{j.material}</td><td className={td}>{j.homework || "-"}</td><td className={`${td} italic text-slate-600`}>{j.teacher_note || "-"}</td>
              <td className={`${td} text-center`}><span className={`px-2 py-0.5 rounded-full text-[10px] border ${UNDERSTANDING[j.understanding || "paham"].cls}`}>{UNDERSTANDING[j.understanding || "paham"].label}</span></td>
              <td className={`${td} text-center whitespace-nowrap text-amber-500`}>{"★".repeat(j.rating)}<span className="text-slate-200">{"★".repeat(5 - j.rating)}</span></td>
            </tr>
          ))}
          {journals.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Belum ada jurnal pada bulan ini.</td></tr>}
        </tbody>
      </table>
      <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-100 px-4 py-2 w-fit ml-auto" data-testid="laporan-avg">
        <span className="text-xs text-slate-500">Rata-rata penilaian</span>
        <span className="flex">{[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
        <span className="font-display font-bold text-sm">{avg.toFixed(1)}</span>
      </div>
      <Signatures right={{ role: "Pengajar", name: signer(settings) }} />
      <Footer settings={settings} />
    </Sheet>
  );
}

/* ---------- JADWAL BELAJAR SISWA ---------- */
const DAY_ORDER = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const endTime = (t, dur = 60) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const e = h * 60 + m + dur; return `${String(Math.floor(e / 60) % 24).padStart(2, "0")}:${String(e % 60).padStart(2, "0")}`; };
export function JadwalSiswa({ settings, student }) {
  const slots = [...(student?.schedule || [])].sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day));
  return (
    <Sheet size="jadwal" testId="doc-jadwal">
      <Header settings={settings} />
      <Title color="bg-sky-100 text-sky-700" sub="Untuk orang tua / wali siswa">JADWAL BELAJAR SISWA</Title>
      <StudentBox student={student} extra={<div className="text-center px-3"><div className="font-display text-xl font-bold">{slots.length}x</div><div className="text-[10px] text-slate-500">per minggu</div></div>} />
      <table className="w-full text-sm">
        <thead><tr className="bg-sky-50 text-sky-700"><th className="py-2 px-3 text-left">Hari</th><th className="py-2 px-3 text-left">Jam Mulai</th><th className="py-2 px-3 text-left">Jam Selesai</th><th className="py-2 px-3 text-center">Durasi</th></tr></thead>
        <tbody>
          {DAY_ORDER.map((d) => {
            const s = slots.find((x) => x.day === d);
            return (
              <tr key={d} className={`border-b border-slate-100 ${s ? "" : "text-slate-300"}`}>
                <td className="py-2.5 px-3 font-semibold">{d}</td><td className="py-2.5 px-3">{s ? s.time : "—"}</td><td className="py-2.5 px-3">{s ? endTime(s.time, s.duration || 60) : "—"}</td><td className="py-2.5 px-3 text-center">{s ? `${s.duration || 60} menit` : "libur"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-[11px] text-slate-600 space-y-1">
        <div className="font-semibold text-slate-800">Catatan untuk orang tua/wali:</div>
        <div>• Mohon hadir 5 menit sebelum jam mulai. Ketidakhadiran diinformasikan kepada pengajar via WhatsApp {settings?.phone}.</div>
        <div>• Setiap sesi yang tidak dihadiri mendapat 1 (satu) kali kesempatan jadwal pengganti.</div>
        <div>• Laporan belajar harian dikirim ke WhatsApp wali setelah sesi selesai.</div>
      </div>
      <Signatures right={{ role: "Pengajar", name: signer(settings) }} />
      <Footer settings={settings} />
    </Sheet>
  );
}
